import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Package, TokenPack, TokenPackPrice } from '../../types';
import { usePurchaseCheckout, type PurchaseTarget } from '../../hooks/usePurchaseCheckout';
import { pickLocalized } from '../../utils/planChange';
import { CouponCodeField } from './CouponCodeField';
import { CouponPicker } from './CouponPicker';

export type SelectedPurchase =
  | { kind: 'token_pack'; tokenPack: TokenPack; price: TokenPackPrice }
  | { kind: 'package'; pkg: Package; billingPeriod: 'month' | 'year' };

interface PurchaseDialogProps {
  selectedPurchase: SelectedPurchase | null;
  initialCoupon?: string | null;
  onClose: () => void;
  onFreeActivated: () => void;
  formatPrice: (amountMinor: string | number, currency: string) => string;
}

const Row: React.FC<{ label: string; value: string; strong?: boolean; accent?: boolean }> = ({
  label,
  value,
  strong,
  accent,
}) => (
  <div className="flex items-center justify-between gap-4 text-sm">
    <span className="text-gray-600 dark:text-slate-400">{label}</span>
    <span
      className={[
        strong ? 'text-base font-semibold' : 'font-medium',
        accent ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-slate-100',
      ].join(' ')}
    >
      {value}
    </span>
  </div>
);

export const PurchaseDialog: React.FC<PurchaseDialogProps> = ({
  selectedPurchase,
  initialCoupon,
  onClose,
  onFreeActivated,
  formatPrice,
}) => {
  const { t, i18n } = useTranslation();
  const target = useMemo<PurchaseTarget | null>(() => {
    if (!selectedPurchase) return null;
    return selectedPurchase.kind === 'package'
      ? {
          kind: 'package',
          itemPublicId: selectedPurchase.pkg.publicId,
          billingPeriod: selectedPurchase.billingPeriod,
        }
      : {
          kind: 'token_pack',
          itemPublicId: selectedPurchase.tokenPack.publicId,
          billingPeriod: 'one_time',
        };
  }, [selectedPurchase]);
  const checkout = usePurchaseCheckout({ target, initialCoupon, onFreeActivated });
  const { quote, pendingOrder, paying, loading } = checkout;
  const item =
    selectedPurchase?.kind === 'package' ? selectedPurchase.pkg : selectedPurchase?.tokenPack;
  const description = item ? pickLocalized(item.description, i18n.language) : null;
  const money = (amount: number) => formatPrice(amount, 'VND');
  const isFree = quote?.amountMinor === 0;
  const busy = paying || loading;

  const errorText = checkout.errorCode
    ? t(
        `subscription.payment.errors.${checkout.errorCode}`,
        checkout.errorMessage ??
          t('subscription.payment.errors.generic', 'Something went wrong. Please try again.')
      )
    : null;

  return (
    <Dialog
      open={Boolean(selectedPurchase)}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('subscription.purchaseDialog.title', 'Confirm Purchase')}</DialogTitle>
      <DialogContent>
        {selectedPurchase && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-800">
              <div className="mb-1 text-base font-semibold text-gray-900 dark:text-slate-100">
                {item?.name}
              </div>
              {description && (
                <p className="text-sm text-gray-600 dark:text-slate-400">{description}</p>
              )}
            </div>

            {pendingOrder ? (
              <Alert severity="info">
                <p className="mb-2">
                  {t(
                    'subscription.payment.pendingOrder',
                    'You already have an unpaid order for this item.'
                  )}{' '}
                  <strong>{money(pendingOrder.amountMinor)}</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="small"
                    variant="contained"
                    disabled={paying}
                    onClick={checkout.continuePending}
                  >
                    {t('subscription.payment.continuePending', 'Continue payment')}
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    disabled={paying}
                    onClick={() => void checkout.cancelPending()}
                  >
                    {t('subscription.payment.cancelAndRechoose', 'Cancel order & choose again')}
                  </Button>
                </div>
              </Alert>
            ) : (
              <>
                <CouponCodeField
                  codes={checkout.codes}
                  maxCodes={checkout.maxCodes}
                  disabled={busy}
                  others={quote?.others}
                  bestCode={quote?.best?.code}
                  onAdd={checkout.addCode}
                  onRemove={checkout.removeCode}
                />

                <CouponPicker
                  target={target}
                  appliedCodes={[...checkout.codes, ...(quote?.best ? [quote.best.code] : [])]}
                  disabled={busy}
                  onPick={checkout.addCode}
                />

                {checkout.quoteChanged && (
                  <Alert severity="warning">
                    {t(
                      'subscription.payment.quoteChanged',
                      'The price changed. Please review the new amount before paying.'
                    )}
                  </Alert>
                )}
                {quote?.best && quote.suggested?.includes(quote.best.code) && (
                  <Alert severity="success">
                    {t(
                      'subscription.coupon.suggested',
                      'A code for you was applied automatically: {{code}}',
                      {
                        code: quote.best.code,
                      }
                    )}
                  </Alert>
                )}

                <div className="space-y-2 rounded-lg border border-gray-200 p-4 dark:border-slate-700">
                  {quote ? (
                    <>
                      <Row
                        label={t('subscription.payment.listPrice', 'Original price')}
                        value={money(quote.listAmountMinor)}
                      />
                      {quote.discountMinor > 0 && (
                        <Row
                          label={t('subscription.payment.discount', 'Discount')}
                          value={`-${money(quote.discountMinor)}`}
                          accent
                        />
                      )}
                      <Row
                        label={t('subscription.payment.total', 'Total to pay')}
                        value={money(quote.amountMinor)}
                        strong
                      />
                    </>
                  ) : (
                    <div className="flex justify-center py-2">
                      <CircularProgress size={22} />
                    </div>
                  )}
                </div>
              </>
            )}

            {errorText && <Alert severity="error">{errorText}</Alert>}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        {!pendingOrder && (
          <Button onClick={() => void checkout.pay()} variant="contained" disabled={busy || !quote}>
            {paying ? (
              <CircularProgress size={18} color="inherit" />
            ) : isFree ? (
              t('subscription.payment.activateFree', 'Activate for free')
            ) : (
              t('subscription.payment.payNow', 'Pay now')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
